import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import html2canvas from "html2canvas";
import dayjs from "dayjs";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/widgets";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Loader2, RefreshCcw } from "lucide-react";
import { ReportToolbar, ReportView } from "@/components/dailyreports/ReportControls";
import { ReportVersions } from "@/components/dailyreports/ReportVersions";
import "./dailyReport.css";

export default function DailyReportsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [dates, setDates] = useState([]);
  const [date, setDate] = useState(null);
  const [reports, setReports] = useState([]);
  const [selectedKey, setSelectedKey] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const cardRef = useRef(null);
  const isAdminOrMd = ["admin", "md"].includes(user?.role);

  const loadDates = useCallback(async () => {
    const r = await api.get("/reports/daily/dates");
    setDates(r.data.dates);
    return r.data.dates;
  }, []);

  const loadReports = useCallback((d) => {
    setLoading(true);
    api
      .get("/reports/daily", { params: d ? { date: d } : {} })
      .then((r) => {
        setReports(r.data.reports);
        setDate(r.data.date);
        setSelectedKey((prev) => {
          if (prev && r.data.reports.some((x) => x.scope_key === prev)) return prev;
          const mine = r.data.mine?.[0];
          return r.data.reports.some((x) => x.scope_key === mine) ? mine : r.data.reports[0]?.scope_key || null;
        });
      })
      .catch((e) => toast.error(errMsg(e)))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    loadDates().then((ds) => loadReports(ds[0] || null)).catch((e) => toast.error(errMsg(e)));
  }, [loadDates, loadReports]);

  const generateNow = async () => {
    setGenerating(true);
    try {
      const r = await api.post("/reports/daily/generate");
      toast.success(`${r.data.reports} reports generated for ${dayjs(r.data.date).format("D MMM")}`);
      const ds = await loadDates();
      loadReports(ds[0]);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setGenerating(false);
    }
  };

  const current = reports.find((r) => r.scope_key === selectedKey) || reports[0];

  const downloadImage = async () => {
    if (!cardRef.current) return;
    setDownloading(true);
    try {
      const canvas = await html2canvas(cardRef.current, { scale: 2, backgroundColor: "#EDEEF2", useCORS: true });
      const a = document.createElement("a");
      a.href = canvas.toDataURL("image/png");
      a.download = `daily_report_${current?.scope_key}_${date}.png`;
      a.click();
      toast.success("Image card downloaded");
    } catch (e) {
      toast.error("Could not render image");
    } finally {
      setDownloading(false);
    }
  };

  const grouped = ["group", "vp", "cluster", "outlet"]
    .map((kind) => ({ kind, items: reports.filter((r) => r.kind === kind) }))
    .filter((g) => g.items.length > 0);

  return (
    <div data-testid="daily-reports-page">
      <PageHeader title="Daily Reports" subtitle="Auto-generated after every upload · gamified scorecards per scope · archive by date">
        {isAdminOrMd && user?.permissions?.can_manage_settings && (
          <Button variant="outline" onClick={generateNow} disabled={generating} data-testid="daily-report-generate-button">
            {generating ? <Loader2 size={15} className="animate-spin mr-1.5" /> : <RefreshCcw size={15} className="mr-1.5" />}
            Generate now
          </Button>
        )}
      </PageHeader>

      <ReportToolbar date={date} dates={dates} loadReports={loadReports}
        selectedKey={selectedKey} setSelectedKey={setSelectedKey} reports={reports}
        grouped={grouped} current={current} downloading={downloading} downloadImage={downloadImage} />

      <ReportView loading={loading} current={current} isAdminOrMd={isAdminOrMd}
        cardRef={cardRef} navigate={navigate} />

      {current && <ReportVersions date={date} scopeKey={current.scope_key} />}
    </div>
  );
}
